import {
	createContext,
	useState,
	Dispatch,
	SetStateAction,
	useMemo,
	MutableRefObject,
	ReactNode,
} from 'react';
import { Geo } from '../../interfaces/geo.interface';
import { Category } from '../../interfaces/category.interface';
import { ProductInterface } from '../../interfaces/product.interface';
import { OrderInterface } from '../../components/OrdersTable/interfaces/Order.interface';
import { Point } from '../../services/orders.services';
import { Contract } from '../../services/contracts.service';
import { OrderCreationFlow } from '../../types/order-flow';
import { UsersInterface } from '../../interfaces/Users.interface';

export interface TruckProps {
	truckType: string | undefined;
	quantity: number | undefined;
	payload: number | undefined;
	deliveryFrom: string | undefined;
	deliveryTo: string | undefined;
	deliveryTimeFrom: string | undefined;
	deliveryTimeTo: string | undefined;
}

export interface OrderProps {
	deliveryAddress?: string;
	deliveryCountryCode?: string;
	deliveryLocation?: Point;
	pickupAddress?: string;
	pickupCountryCode?: string;
	pickupLocation?: Point;
	location?: Geo;
	group?: Category;
	category?: Category;
	product?: ProductInterface;
	locationPhoto?: string;
	trucks?: TruckProps[];
	deliveryIncluded?: boolean;
	contractData?: Contract;
	producer?: UsersInterface;
	quantity?: number;
}

export interface AdditionalProps {
	orderModalRef: MutableRefObject<HTMLDivElement | null>;
	order: OrderInterface | undefined;
	dontShowAnymore?: boolean;
	orderFlow?: OrderCreationFlow;
}

interface OrderContextProps {
	order: OrderProps | undefined;
	setOrder: Dispatch<SetStateAction<OrderProps | undefined>>;
	search: string | undefined;
	setSearch: Dispatch<SetStateAction<string | undefined>>;
	selectedOrderId: number | undefined;
	setOrderId: Dispatch<SetStateAction<number | undefined>>;
	additional: AdditionalProps;
	setAdditional: Dispatch<SetStateAction<AdditionalProps>>;
}

export const OrderContext = createContext<OrderContextProps>({
	order: undefined,
	setOrder: () => null,
	selectedOrderId: undefined,
	setOrderId: () => null,
	search: undefined,
	setSearch: () => null,
	additional: {
		orderModalRef: { current: null },
		order: undefined,
		dontShowAnymore: false,
	},
	setAdditional: () => {},
});

const OrderContextProvider = ({ children }: { children: ReactNode }) => {
	const [order, setOrder] = useState<OrderProps>();
	const [selectedOrderId, setOrderId] = useState<number>();
	const [search, setSearch] = useState<string>();
	const [additional, setAdditional] = useState<AdditionalProps>({
		orderModalRef: { current: null },
		order: undefined,
		dontShowAnymore: false,
		orderFlow: 'BASE',
	});

	const value = useMemo<OrderContextProps>(
		() => ({
			order,
			setOrder,
			selectedOrderId,
			setOrderId,
			search,
			setSearch,
			additional,
			setAdditional,
		}),
		[order, search, selectedOrderId, additional],
	);

	return (
		<OrderContext.Provider value={value}>{children}</OrderContext.Provider>
	);
};

export default OrderContextProvider;
